import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import _ from 'underscore';

import services from 'api/services';
import { useFreecodeContextData } from 'hooks/useContext';
import FormModal from 'components/Modals/formModal';
import { ModalInput } from './styles';
import { ObjectException } from 'global/errors';
import addFreecodeGraphics from 'components/FreecodePlayer/addFreecodeGraphics';
import { ModalTextArea } from 'components/Modals/styles';

const { FreecodeCreatorService } = services;

export default function GraphicsDataModal({ graphicsIndex, closeModal }) {
  const {
    setTestImpl,
    testImpl: { _id, test, graphics },
  } = useFreecodeContextData();

  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(undefined);

  useEffect(() => {
    if (!loading || image || !graphics?.[graphicsIndex]) return;
    let graphicsImage = graphics[graphicsIndex];
    setImage({
      ...graphicsImage,
      jsonInput: graphicsImage.jsonData ? JSON.stringify(graphicsImage.jsonData, null, 2) : '',
    });
    setLoading(false);
  }, [loading, image, graphics, graphicsIndex]);

  const handleGenerateAnimation = (jsonData = '') => {
    if (loading) return;
    let sheetData;
    try {
      jsonData = fixKeyNames(jsonData);
      sheetData = getJsonSheetAsObject(jsonData);
    } catch (error) {
      toast.error("Can't process invalid JSON\nNeed frames defined to generate animation");
      return;
    }
    if (!sheetData || !sheetData.frames || _.keys(sheetData.frames).length <= 0) {
      toast.error('Need frames defined to generate animation');
      return;
    }
    let sortedFrameNames = _.sortBy(_.keys(sheetData.frames), function (frameName) {
      let picIndexArray = frameName.match(/\d+/g);
      return picIndexArray && picIndexArray[0] ? parseInt(picIndexArray[picIndexArray.length - 1]) : -1;
    });

    if (!sheetData.animations) sheetData.animations = {};
    sheetData.animations['default'] = sortedFrameNames;

    let updatedImage = { ...image };
    if (!updatedImage.jsonData) updatedImage.jsonData = {};
    updatedImage.jsonData = sheetData;
    updatedImage.jsonInput = JSON.stringify(sheetData, null, 2);
    setImage(updatedImage);
  };

  const handleOnConfirm = () => {
    if (loading) return toast.error('Already loading');
    setLoading(true);

    const doConfirm = async () => {
      try {
        let update = [...graphics];
        if (image.description) update[graphicsIndex].description = image.description;
        try {
          update[graphicsIndex].jsonData = getJsonSheetAsObject(fixKeyNames(image.jsonInput));
        } catch (e) {
          throw new ObjectException('invalid JSON', 'request error');
        }

        let response = await FreecodeCreatorService.saveGraphics(test, _id, update);
        if (response.error)
          throw new ObjectException(response.error?.message || 'Graphics update filed', 'response error');
        if (!response.graphics) throw new ObjectException('Response missing graphics', 'response error');

        setTestImpl((oldTestImpl) => ({ ...oldTestImpl, graphics: response.graphics }));
        await addFreecodeGraphics(update[graphicsIndex]);
        toast.success('Image data updated');
        closeModal();
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'Could not upload graphics');
      } finally {
        setLoading(false);
      }
    };
    doConfirm();
  };

  const fixKeyNames = (jsonData) => {
    let tmpObj = getJsonSheetAsObject(jsonData);
    if (tmpObj.frames) {
      _.keys(tmpObj.frames).forEach(function (oldKey) {
        if (oldKey.includes('.')) {
          let newKey = oldKey.replace(/\./g, '_');
          tmpObj.frames[newKey] = tmpObj.frames[oldKey];
          delete tmpObj.frames[oldKey];
        }
      });
    }
    return JSON.stringify(tmpObj, null, 2);
  };

  // Convert json sheet string to object
  const getJsonSheetAsObject = (jsonData) => {
    if (typeof jsonData === 'string') {
      // regex to replace non-escaped single quotes (for easy copy-paste)
      // Use only standard regex operations with good browser support
      let fixedString = jsonData.replace(/^'/, '"');
      while (/[^\\]'/.test(fixedString)) {
        fixedString = fixedString.replace(/[^\\]'/g, function (str) {
          return str[0] + '"';
        });
      }
      return JSON.parse(fixedString);
    }
    return JSON.parse(JSON.stringify(jsonData, null, 2));
  };

  return (
    <FormModal
      title="Configure image JSON data"
      confirmButtonText="Save"
      onCancel={closeModal}
      onConfirm={handleOnConfirm}
      additionalActionButton={[{ text: 'Generate Animation', onClick: () => handleGenerateAnimation(image.jsonInput) }]}
      loading={loading}
    >
      Image: {image?.name}
      <ModalInput
        value={image?.description || ''}
        onChange={(e) => setImage({ ...image, description: e.target.value })}
        placeholder="Optional description"
      ></ModalInput>
      <ModalTextArea
        value={image?.jsonInput}
        onChange={(e) => setImage({ ...image, jsonInput: e.target.value })}
        placeholder="JSON recommended to be auto generated by a tool such as spritesheet.js https://github.com/krzysztof-o/spritesheet.js"
      />
    </FormModal>
  );
}
